<script setup>
import { computed, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { api } from '../services/api'
import AppDrawer from '../components/AppDrawer.vue'
import StudentProgressPanel from '../components/admin/StudentProgressPanel.vue'

const route = useRoute()
const users = ref([])
const loading = ref(true)
const saving = ref(false)
const error = ref('')
const formError = ref('')
const toast = ref('')
const search = ref('')
const filter = ref('student')
const gradeFilter = ref('')
// drawer: '' (đóng) | 'progress' | 'form'
const drawer = ref('')
const selectedId = ref(null)
const linkSearch = ref('')
const blank = role => ({ full_name: '', email: '', password: '', role, grade: 6, parent_ids: [], student_ids: [] })
const form = ref(blank('student'))
const roles = { student: 'Học sinh', parent: 'Phụ huynh', admin: 'Quản trị viên' }
let toastTimer

const selected = computed(() => users.value.find(user => user.id === selectedId.value))
const isEditing = computed(() => drawer.value === 'form' && !!selectedId.value)
const counts = computed(() => Object.fromEntries(Object.keys(roles).map(role => [role, users.value.filter(user => user.role === role).length])))
const filtered = computed(() => {
  const query = search.value.trim().toLowerCase()
  return users.value.filter(user => user.role === filter.value
    && (filter.value !== 'student' || !gradeFilter.value || user.grade === gradeFilter.value)
    && `${user.full_name} ${user.email}`.toLowerCase().includes(query))
})
const studentGrades = computed(() => [...new Set(users.value.filter(user => user.role === 'student').map(user => user.grade))].sort((a, b) => a - b))
// Danh sách tài khoản có thể liên kết với tài khoản đang soạn (học sinh ↔ phụ huynh).
const linkField = computed(() => form.value.role === 'student' ? 'parent_ids' : form.value.role === 'parent' ? 'student_ids' : '')
const linkCandidates = computed(() => {
  const role = form.value.role === 'student' ? 'parent' : 'student'
  const query = linkSearch.value.trim().toLowerCase()
  return users.value.filter(user => user.role === role && user.id !== selectedId.value && `${user.full_name} ${user.email}`.toLowerCase().includes(query))
})

function name(id) { return users.value.find(user => user.id === id)?.full_name }
function linkedNames(user) {
  const ids = user.role === 'student' ? user.parent_ids : user.role === 'parent' ? user.student_ids : []
  return ids.map(name).filter(Boolean).join(', ')
}

function showToast(message) {
  toast.value = message
  clearTimeout(toastTimer)
  toastTimer = setTimeout(() => toast.value = '', 3000)
}

async function load() {
  try { users.value = (await api('/admin/users')).users; error.value = '' }
  catch (err) { error.value = err.message }
  finally { loading.value = false }
}

function openCreate() {
  selectedId.value = null
  form.value = { ...blank(filter.value), grade: gradeFilter.value || 6 }
  formError.value = ''
  linkSearch.value = ''
  drawer.value = 'form'
}

function openEdit(user) {
  selectedId.value = user.id
  form.value = { ...blank(user.role), ...user, password: '', parent_ids: [...user.parent_ids], student_ids: [...user.student_ids] }
  formError.value = ''
  linkSearch.value = ''
  drawer.value = 'form'
}

function openProgress(user) {
  selectedId.value = user.id
  drawer.value = 'progress'
}

function closeDrawer() {
  if (!saving.value) drawer.value = ''
}

async function save() {
  saving.value = true
  formError.value = ''
  try {
    const payload = { ...form.value }
    if (!payload.password) delete payload.password
    const path = isEditing.value ? `/admin/users/${selectedId.value}` : '/admin/users'
    const saved = await api(path, { method: isEditing.value ? 'PUT' : 'POST', body: JSON.stringify(payload) })
    showToast(isEditing.value ? 'Đã lưu thông tin tài khoản' : `Đã tạo tài khoản ${saved.user.full_name}`)
    await load()
    filter.value = saved.user.role
    drawer.value = ''
  } catch (err) { formError.value = err.message }
  finally { saving.value = false }
}

onMounted(async () => {
  await load()
  if (roles[route.query.tao]) {
    filter.value = route.query.tao
    openCreate()
  }
})
</script>

<template>
  <header><p class="eyebrow">Quản trị tài khoản</p><h1 class="page-title">Học sinh & tài khoản</h1><p class="page-subtitle">Theo dõi việc học của từng em, tạo tài khoản và liên kết học sinh với phụ huynh.</p></header>
  <div v-if="error" class="error-banner" role="alert" style="margin-top:20px">{{ error }} <button class="btn btn-light" @click="load">Tải lại</button></div>
  <div v-if="loading" class="loading-block"><div><div class="loader"></div><p>Đang tải tài khoản…</p></div></div>

  <section v-else class="card accounts">
    <div class="accounts-toolbar">
      <div class="semester-tabs user-tabs" role="tablist" aria-label="Loại tài khoản">
        <button v-for="(label, role) in roles" :key="role" type="button" role="tab" :aria-selected="filter === role" :class="{ active: filter === role }" @click="filter = role">{{ label }} <span>{{ counts[role] }}</span></button>
      </div>
      <select v-if="filter === 'student' && studentGrades.length > 0" v-model.number="gradeFilter" class="form-control accounts-grade" aria-label="Lọc theo lớp"><option value="">Tất cả lớp</option><option v-for="grade in studentGrades" :key="grade" :value="grade">Lớp {{ grade }}</option></select>
      <input v-model="search" class="form-control accounts-search" type="search" :placeholder="`Tìm ${roles[filter].toLowerCase()} theo tên hoặc email`" aria-label="Tìm tài khoản">
      <button type="button" class="btn btn-primary" @click="openCreate">＋ Thêm {{ roles[filter].toLowerCase() }}</button>
    </div>

    <div class="accounts-table" :class="`is-${filter}`">
      <div class="accounts-row accounts-head" aria-hidden="true">
        <span>{{ roles[filter] }}</span>
        <template v-if="filter === 'student'"><span>Lớp</span><span>Phụ huynh</span><span>Việc học</span></template>
        <span v-else-if="filter === 'parent'">Con được theo dõi</span>
        <span v-else>Quyền</span>
        <span></span>
      </div>

      <div v-if="!filtered.length" class="empty-state">
        <strong>{{ search || gradeFilter ? 'Không tìm thấy tài khoản phù hợp' : `Chưa có ${roles[filter].toLowerCase()}` }}</strong>
        <button v-if="!search" type="button" class="btn btn-light" style="margin-top:12px" @click="openCreate">＋ Thêm {{ roles[filter].toLowerCase() }}</button>
      </div>

      <div v-for="user in filtered" :key="user.id" class="accounts-row" :class="{ clickable: user.role === 'student' }" @click="user.role === 'student' ? openProgress(user) : openEdit(user)">
        <div class="accounts-person"><span class="avatar user-avatar">{{ user.avatar }}</span><div><strong>{{ user.full_name }}</strong><small>{{ user.email }}</small></div></div>
        <template v-if="user.role === 'student'">
          <span class="accounts-cell" data-label="Lớp">{{ user.grade }}</span>
          <span class="accounts-cell" data-label="Phụ huynh" :class="{ muted: !linkedNames(user) }">{{ linkedNames(user) || 'Chưa liên kết' }}</span>
          <span class="accounts-cell" data-label="Việc học"><b>{{ user.completed_lessons }} bài xong</b><small>{{ user.last_active ? `Hoạt động ${user.last_active}` : 'Chưa bắt đầu học' }}</small></span>
        </template>
        <span v-else-if="user.role === 'parent'" class="accounts-cell" data-label="Con được theo dõi" :class="{ muted: !linkedNames(user) }">{{ linkedNames(user) || 'Chưa liên kết học sinh' }}</span>
        <span v-else class="accounts-cell muted" data-label="Quyền">Quản lý nội dung và tài khoản</span>
        <div class="accounts-actions" @click.stop>
          <button v-if="user.role === 'student'" type="button" class="btn btn-light btn-sm" @click="openProgress(user)">Tiến độ</button>
          <button type="button" class="btn btn-light btn-sm" @click="openEdit(user)">Sửa</button>
        </div>
      </div>
    </div>
  </section>

  <AppDrawer :open="drawer === 'progress' && !!selected" wide :eyebrow="`Học sinh · Lớp ${selected?.grade}`" :title="selected?.full_name || ''" @close="closeDrawer">
    <template #subtitle><p class="drawer-sub">{{ selected?.email }}<template v-if="selected && linkedNames(selected)"> · Phụ huynh: {{ linkedNames(selected) }}</template></p></template>
    <template #actions><button type="button" class="btn btn-light btn-sm" @click="openEdit(selected)">Sửa tài khoản</button></template>
    <StudentProgressPanel v-if="selected" :student="selected" @notice="showToast" />
  </AppDrawer>

  <AppDrawer :open="drawer === 'form'" :eyebrow="isEditing ? 'Sửa tài khoản' : 'Tạo tài khoản'" :title="isEditing ? form.full_name || 'Tài khoản' : `${roles[form.role]} mới`" @close="closeDrawer">
    <form id="account-form" class="account-form" @submit.prevent="save">
      <div v-if="formError" class="error-banner" role="alert">{{ formError }}</div>
      <div class="form-group">
        <label>Loại tài khoản</label>
        <div class="role-choice" role="radiogroup" aria-label="Loại tài khoản">
          <label v-for="(label, role) in roles" :key="role" :class="{ active: form.role === role }"><input v-model="form.role" type="radio" :value="role" :disabled="saving">{{ label }}</label>
        </div>
      </div>
      <h3 class="account-form-section">Thông tin đăng nhập</h3>
      <div class="form-group"><label for="account-name">Họ và tên</label><input id="account-name" v-model="form.full_name" class="form-control" required minlength="2" placeholder="Nguyễn Minh An" :disabled="saving"></div>
      <div class="form-group"><label for="account-email">Email đăng nhập</label><input id="account-email" v-model="form.email" class="form-control" type="email" required placeholder="ten@example.com" :disabled="saving"></div>
      <div class="form-group"><label for="account-password">{{ isEditing ? 'Mật khẩu mới' : 'Mật khẩu ban đầu' }}</label><input id="account-password" v-model="form.password" class="form-control" type="password" :required="!isEditing" minlength="8" autocomplete="new-password" :placeholder="isEditing ? 'Để trống nếu giữ nguyên' : 'Ít nhất 8 ký tự'" :disabled="saving"></div>
      <template v-if="form.role === 'student'">
        <h3 class="account-form-section">Học tập</h3>
        <div class="form-group"><label for="account-grade">Lớp đang học</label><select id="account-grade" v-model.number="form.grade" class="form-control" :disabled="saving"><option v-for="grade in 12" :key="grade" :value="grade">Lớp {{ grade }}</option></select><small class="form-help">Học sinh được đăng ký tự động các môn đang mở của lớp này.</small></div>
      </template>
      <template v-if="linkField">
        <h3 class="account-form-section">{{ form.role === 'student' ? 'Phụ huynh theo dõi' : 'Con được theo dõi' }} <span v-if="form[linkField].length" class="chip">{{ form[linkField].length }} đã chọn</span></h3>
        <input v-if="linkCandidates.length > 6 || linkSearch" v-model="linkSearch" class="form-control link-search" type="search" placeholder="Tìm để liên kết…" aria-label="Tìm tài khoản để liên kết">
        <div v-if="linkCandidates.length" class="link-options">
          <label v-for="item in linkCandidates" :key="item.id"><input v-model="form[linkField]" type="checkbox" :value="item.id" :disabled="saving"><span>{{ item.full_name }}<small>{{ item.role === 'student' ? `Lớp ${item.grade} · ` : '' }}{{ item.email }}</small></span></label>
        </div>
        <p v-else class="form-help">{{ linkSearch ? 'Không tìm thấy tài khoản phù hợp.' : form.role === 'student' ? 'Chưa có tài khoản phụ huynh nào. Có thể liên kết sau.' : 'Chưa có học sinh nào để liên kết.' }}</p>
      </template>
      <p v-if="form.role === 'admin'" class="form-help">Quản trị viên có toàn quyền soạn nội dung và quản lý tài khoản.</p>
    </form>
    <template #footer>
      <button type="submit" form="account-form" class="btn btn-primary" :disabled="saving">{{ saving ? 'Đang lưu…' : isEditing ? 'Lưu thay đổi' : 'Tạo tài khoản' }}</button>
      <button type="button" class="btn btn-light" :disabled="saving" @click="closeDrawer">Hủy</button>
    </template>
  </AppDrawer>
  <div v-if="toast" class="toast" role="status">✓ {{ toast }}</div>
</template>

<style scoped>
.accounts { margin-top: 22px; overflow: hidden; }
.accounts-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 12px; padding: 16px 20px; border-bottom: 1px solid var(--border); }
.accounts-toolbar .user-tabs { margin: 0; }
.accounts-search { flex: 1; min-width: 200px; height: 42px; }
.accounts-grade { width: auto; min-width: 130px; height: 42px; }
.accounts-row { display: grid; align-items: center; gap: 16px; padding: 13px 20px; border-bottom: 1px solid var(--border); }
.accounts-row:last-child { border-bottom: 0; }
.is-student .accounts-row { grid-template-columns: minmax(220px, 2fr) 70px minmax(120px, 1.3fr) minmax(130px, 1.2fr) auto; }
.is-parent .accounts-row, .is-admin .accounts-row { grid-template-columns: minmax(220px, 2fr) minmax(160px, 2fr) auto; }
.accounts-head { padding-top: 10px; padding-bottom: 10px; background: #fafaff; color: #8d91ad; font-size: 11px; font-weight: 800; letter-spacing: .4px; text-transform: uppercase; }
.accounts-row.clickable { cursor: pointer; }
.accounts-row:not(.accounts-head):hover { background: #faf9ff; }
.accounts-person { display: flex; align-items: center; gap: 12px; min-width: 0; }
.accounts-person > div { min-width: 0; }
.accounts-person strong, .accounts-person small { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.accounts-person strong { font-size: 14px; }
.accounts-person small { margin-top: 2px; color: var(--muted); font-size: 12px; }
.user-avatar { flex: 0 0 40px; width: 40px; height: 40px; font-size: 13px; }
.accounts-cell { min-width: 0; font-size: 13px; overflow-wrap: anywhere; }
.accounts-cell.muted { color: #a0a5b9; }
.accounts-cell b, .accounts-cell small { display: block; }
.accounts-cell b { font-size: 13px; }
.accounts-cell small { margin-top: 2px; color: var(--muted); font-size: 11px; }
.accounts-actions { display: flex; justify-content: flex-end; gap: 6px; }
.drawer-sub { margin: 4px 0 0; color: var(--muted); font-size: 12px; }
.account-form-section { display: flex; align-items: center; gap: 8px; margin: 22px 0 12px; padding-top: 18px; border-top: 1px solid var(--border); font-size: 14px; }
.role-choice { display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px; padding: 4px; border: 1px solid var(--border); border-radius: 12px; background: #fafaff; }
.role-choice label { margin: 0; padding: 9px 6px; border-radius: 9px; color: var(--muted); font-size: 12px; font-weight: 700; text-align: center; cursor: pointer; }
.role-choice label.active { background: var(--teal); color: #fff; }
.role-choice input { position: absolute; opacity: 0; pointer-events: none; }
.link-search { height: 40px; margin-bottom: 8px; }
.link-options { max-height: 260px; }
.link-options small { display: block; margin-top: 2px; color: var(--muted); font-size: 11px; }
@media (max-width: 860px) {
  .accounts-head { display: none; }
  .is-student .accounts-row, .is-parent .accounts-row, .is-admin .accounts-row { grid-template-columns: minmax(0, 1fr) auto; gap: 8px 12px; }
  .accounts-person { grid-column: 1 / -1; }
  .accounts-cell { grid-column: 1; font-size: 12px; }
  .accounts-cell::before { content: attr(data-label) ': '; color: #a0a5b9; }
  .accounts-cell b, .accounts-cell small { display: inline; }
  .accounts-cell small::before { content: ' · '; }
  .accounts-actions { grid-column: 2; grid-row: 2 / span 3; align-self: center; flex-direction: column; }
}
@media (max-width: 560px) {
  .accounts-toolbar { padding: 14px; }
  .accounts-toolbar .btn { width: 100%; }
  .accounts-row { padding: 13px 14px; }
}
</style>
